/**
 * 🌍 GLOBAL STANDART KOD (TÜM PROJELER İÇİN GEÇERLİDİR)
 *
 * Ortak Destek Kaydı Formu — AI Asistan (AiChat) ve Global Chat (GlobalVoiceAssistant)
 * tarafından PAYLAŞILIR. Tek kaynak `qubepro-core`; senkron ile diğer repolara yayılır.
 * Sunumsal (controlled): değerler dışarıdan; onChange ile güncellenir.
 */
import React, { useId } from 'react';
import { Building2, Camera, CloudCog, Paperclip, TicketCheck, X } from 'lucide-react';
import './SupportTicketForm.css';

import { supportFormCopy, supportTargetCopy, supportTargetLabel, type SupportTarget } from './support-ticket-scope';
import { supportFieldErrorMessage, supportValidationCopy, validateSupportForm, type SupportValidationField } from './support-ticket-validation';
export type { SupportTarget } from './support-ticket-scope';

export interface SupportFormState {
  supportTarget: SupportTarget | null;
  title: string;
  category: string;
  priority: string;
  description: string;
  steps: string;
}

export const SUPPORT_FORM_INITIAL: SupportFormState = {
  supportTarget: null,
  title: '',
  category: 'REQUEST',
  priority: 'medium',
  description: '',
  steps: '',
};

export const SUPPORT_CATEGORY_OPTIONS = [
  { value: 'REQUEST', label: 'Talep' },
  { value: 'ISSUE', label: 'Hata' },
  { value: 'SUGGESTION', label: 'Öneri' },
  { value: 'QUESTION', label: 'Soru' },
  { value: 'OTHER', label: 'Diğer' },
];

const SUPPORT_CATEGORY_LABELS: Record<string, string> = {
  REQUEST: 'Talep',
  ISSUE: 'Hata',
  SUGGESTION: 'Öneri',
  QUESTION: 'Soru',
  DEVELOPMENT: 'Talep',
  OTHER: 'Diğer',
};

export function normalizeSupportCategory(value: string): string {
  const normalized = value.trim();
  return normalized === 'DEVELOPMENT' ? 'REQUEST' : normalized;
}

export function getSupportCategoryLabel(value: string): string {
  const normalized = normalizeSupportCategory(value);
  return SUPPORT_CATEGORY_LABELS[normalized] || normalized || '-';
}

export const SUPPORT_PRIORITY_OPTIONS = [
  { value: 'low', label: 'Düşük' },
  { value: 'medium', label: 'Orta' },
  { value: 'high', label: 'Yüksek' },
  { value: 'urgent', label: 'Acil' },
];

export function supportFormHasContent(form: SupportFormState): boolean {
  return Boolean(form.title.trim() || form.description.trim() || form.steps.trim());
}

/** AI'a gönderilecek prompt metni (taslak hazırlatma). */
export function buildSupportMessageFromForm(form: SupportFormState, fallbackText: string): string {
  const lines = [
    'Destek kaydı taslağı hazırla.',
    `Destek hedefi: ${supportTargetLabel(form.supportTarget)}`,
    form.title.trim() ? `Başlık: ${form.title.trim()}` : '',
    form.category.trim() ? `Kategori: ${getSupportCategoryLabel(form.category.trim())}` : '',
    form.priority.trim() ? `Öncelik: ${form.priority.trim()}` : '',
    form.description.trim() ? `Açıklama: ${form.description.trim()}` : '',
    form.steps.trim() ? `Tekrarlama adımları: ${form.steps.trim()}` : '',
    fallbackText.trim() ? `Ek not: ${fallbackText.trim()}` : '',
  ].filter(Boolean);
  return lines.join('\n');
}

/** Kullanıcı balonunda gösterilecek özet. */
export function buildSupportDisplayMessage(form: SupportFormState, fallbackText: string): string {
  const title = form.title.trim() || 'Destek kaydı taslağı';
  const details = [
    `Destek hedefi: ${supportTargetLabel(form.supportTarget)}`,
    `Başlık: ${title}`,
    `Kategori: ${getSupportCategoryLabel(form.category)}`,
    `Öncelik: ${form.priority || '-'}`,
    form.description.trim() ? `Açıklama: ${form.description.trim()}` : '',
    form.steps.trim() ? `Tekrarlama adımları: ${form.steps.trim()}` : '',
    fallbackText.trim() ? `Ek not: ${fallbackText.trim()}` : '',
  ].filter(Boolean);
  return details.join('\n');
}

export interface SupportTicketFormProps {
  language?: string;
  /** Conversation purpose already determines the recipient. */
  targetLocked?: boolean;
  value: SupportFormState;
  onChange: (field: keyof SupportFormState, value: string) => void;
  onClose?: () => void;
  busy?: boolean;
  descriptionRef?: React.Ref<HTMLTextAreaElement>;
  title?: string;
  /** Verilirse formun altında birincil "gönder" butonu gösterilir. */
  onSubmit?: () => void;
  /** Gönder butonunun etkin olup olmadığı (genelde formda içerik var mı). */
  canSubmit?: boolean;
  /** Explain a consumer-owned block such as a screenshot awaiting upload. */
  submitDisabledReason?: string;
  /** Gönder butonu etiketi. */
  submitLabel?: string;
  /** Verilirse ekran görüntüsü ekle butonu gösterilir. */
  onCaptureScreenshot?: () => void;
  /** Verilirse dosya/görsel ekle butonu gösterilir. */
  onAttachFile?: () => void;
  /** Eklenmiş dosya sayısı (rozet için). */
  attachmentCount?: number;
  /** Localized, safe submission failure shown beside the preserved form. */
  error?: string;
}

export function SupportTicketForm({
  language = 'tr',
  targetLocked = false,
  value,
  onChange,
  onClose,
  busy = false,
  descriptionRef,
  title,
  onSubmit,
  canSubmit = true,
  submitDisabledReason,
  submitLabel,
  onCaptureScreenshot,
  onAttachFile,
  attachmentCount = 0,
  error,
}: SupportTicketFormProps) {
  const id = useId();
  const copy = supportFormCopy(language);
  const targetCopy = supportTargetCopy(language);
  const validationCopy = supportValidationCopy(language);
  const errors = validateSupportForm(value);
  const valid = Object.keys(errors).length === 0;
  const fieldError = (field: SupportValidationField) => errors[field] ? (
    <small id={`${id}-${field}-error`} className="support-ticket-form__error" aria-live="polite">
      {supportFieldErrorMessage(errors[field]!, language)}
    </small>
  ) : null;
  const fieldA11y = (field: SupportValidationField) => ({
    'aria-invalid': Boolean(errors[field]),
    'aria-describedby': errors[field] ? `${id}-${field}-error` : `${id}-requirements`,
  });
  const resolvedSubmitLabel = submitLabel ?? copy.submit;
  const normalizedCategory = normalizeSupportCategory(value.category);
  const categoryValue = SUPPORT_CATEGORY_OPTIONS.some((option) => option.value === normalizedCategory)
    ? normalizedCategory
    : SUPPORT_FORM_INITIAL.category;

  return (
    <div className="support-ticket-form" aria-label={copy.form}>
      <div className="support-ticket-form__header">
        <span>
          <TicketCheck size={14} />
          {title ?? copy.form}
        </span>
        {onClose && (
          <button
            type="button"
            className="btn-secondary"
            onClick={onClose}
            title={copy.close}
            aria-label={copy.close}
            disabled={busy}
          >
            <X size={14} />
          </button>
        )}
      </div>

      <p role="status">{value.supportTarget ? targetCopy.review : targetCopy.choose}</p>
      {targetLocked ? <p className="support-ticket-form__destination">
        {copy.target}: <strong>{supportTargetLabel(value.supportTarget, language)}</strong>
      </p> : <fieldset className="support-ticket-form__target" disabled={busy}>
        <legend>{copy.target}</legend>
        <label className={value.supportTarget === 'QUBEPRO_SAAS' ? 'is-selected' : ''}>
          <input
            type="radio"
            name="supportTarget"
            value="QUBEPRO_SAAS"
            checked={value.supportTarget === 'QUBEPRO_SAAS'}
            onChange={() => onChange('supportTarget', 'QUBEPRO_SAAS')}
          />
          <CloudCog size={16} aria-hidden />
          <span>
            <strong>{targetCopy.vendor}</strong>
            <small>{copy.vendorHint}</small>
          </span>
        </label>
        <label className={value.supportTarget === 'TENANT_INTERNAL' ? 'is-selected' : ''}>
          <input
            type="radio"
            name="supportTarget"
            value="TENANT_INTERNAL"
            checked={value.supportTarget === 'TENANT_INTERNAL'}
            onChange={() => onChange('supportTarget', 'TENANT_INTERNAL')}
          />
          <Building2 size={16} aria-hidden />
          <span>
            <strong>{targetCopy.internal}</strong>
            <small>{copy.internalHint}</small>
          </span>
        </label>
      </fieldset>}
      {fieldError('supportTarget')}
      <p id={`${id}-requirements`} className="support-ticket-form__hint">
        {validationCopy.required} {validationCopy.ranges}
      </p>
      {error && <p className="support-ticket-form__error" role="alert">{error}</p>}

      <div className="support-ticket-form__grid">
        <label className="support-ticket-form__field support-ticket-form__field--title">
          <span>{copy.title}</span>
          <input
            aria-label={copy.title}
            {...fieldA11y('title')}
            value={value.title}
            onChange={(e) => onChange('title', e.target.value)}
            placeholder={copy.titleHint}
            disabled={busy}
          />
          {fieldError('title')}
        </label>
        <label className="support-ticket-form__field">
          <span>{copy.category}</span>
          <select aria-label={copy.category} {...fieldA11y('category')} value={categoryValue} onChange={(e) => onChange('category', e.target.value)} disabled={busy}>
            {SUPPORT_CATEGORY_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>{copy.categories[option.value as keyof typeof copy.categories]}</option>
            ))}
          </select>
          {fieldError('category')}
        </label>
        <label className="support-ticket-form__field">
          <span>{copy.priority}</span>
          <select aria-label={copy.priority} {...fieldA11y('priority')} value={value.priority} onChange={(e) => onChange('priority', e.target.value)} disabled={busy}>
            {SUPPORT_PRIORITY_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>{copy.priorities[option.value as keyof typeof copy.priorities]}</option>
            ))}
          </select>
          {fieldError('priority')}
        </label>
      </div>

      <label className="support-ticket-form__field">
        <span>{copy.description}</span>
        <textarea
          aria-label={copy.description}
          {...fieldA11y('description')}
          ref={descriptionRef}
          value={value.description}
          onChange={(e) => onChange('description', e.target.value)}
          placeholder={copy.descriptionHint}
          rows={3}
          disabled={busy}
        />
        {fieldError('description')}
      </label>

      <label className="support-ticket-form__field">
        <span>{copy.steps}</span>
        <textarea
          aria-label={copy.steps}
          {...fieldA11y('steps')}
          value={value.steps}
          onChange={(e) => onChange('steps', e.target.value)}
          placeholder={copy.stepsHint}
          rows={2}
          disabled={busy}
        />
        {fieldError('steps')}
      </label>

      {(onSubmit || onCaptureScreenshot || onAttachFile) && (
        <div className="support-ticket-form__actions">
          <div className="support-ticket-form__tools">
            {onCaptureScreenshot && (
              <button
                type="button"
                className="btn-secondary"
                onClick={onCaptureScreenshot}
                disabled={busy || !value.supportTarget}
                title={copy.capture}
                aria-label={copy.capture}
              >
                <Camera size={15} />
              </button>
            )}
            {onAttachFile && (
              <button
                type="button"
                className="btn-secondary"
                onClick={onAttachFile}
                disabled={busy || !value.supportTarget}
                title={copy.attach}
                aria-label={copy.attach}
              >
                <Paperclip size={15} />
                {attachmentCount > 0 && (
                  <span className="support-ticket-form__tool-badge">{attachmentCount}</span>
                )}
              </button>
            )}
          </div>
          {onSubmit && (
            <button
              type="button"
              className="btn-primary"
              onClick={() => { if (valid && canSubmit && !busy) onSubmit(); }}
              disabled={busy || !canSubmit || !valid}
              title={!value.supportTarget ? targetCopy.choose : !valid ? validationCopy.invalid : canSubmit ? resolvedSubmitLabel : submitDisabledReason ?? copy.needText}
            >
              <TicketCheck size={14} />
              <span>{busy ? copy.sending : resolvedSubmitLabel}</span>
            </button>
          )}
        </div>
      )}
    </div>
  );
}

export default SupportTicketForm;
